import React, { useState, useEffect } from 'react';
import { Card, List, Badge, Button, Space, message, Tabs, Form, Input, InputNumber, DatePicker, Row, Col } from 'antd';
import { PlusOutlined, FileTextOutlined, ReloadOutlined, SafetyCertificateOutlined, DownloadOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import IntlMessage from "../../../../components/IntlMessage/IntlMessage";
import ApplicationService from '../../../../services/ApplicationService';
import { APPLICATION_STATUS, getStatusLabel } from '../../../../constants/applicationStatus';
import { signApplication } from '../../../../utils/signApplication';
import dayjs from 'dayjs';

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;
const UserApplications = () => {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const [signingLoading, setSigningLoading] = useState({});
    const [downloadLoading, setDownloadLoading] = useState({});
    const { user } = useSelector(state => state.user);
    const { t } = useTranslation();

    useEffect(() => {
        fetchMyApplications();
    }, []);

    const fetchMyApplications = async () => {
        setLoading(true);
        try {
            const response = await ApplicationService.getMyApplications();
            setApplications(response.data || []);
        } catch (error) {
            message.error(<IntlMessage id={'dashboard.user.applications.fetchError'} fallback={'Не удалось загрузить заявки'} />);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateApplication = async (values) => {
        setSubmitting(true);
        try {
            const monthlyVolumes = {};
            if (values.monthMarch) monthlyVolumes['март'] = values.monthMarch;
            if (values.monthApril) monthlyVolumes['апрель'] = values.monthApril;
            if (values.monthMay) monthlyVolumes['май'] = values.monthMay;
            if (values.monthJune) monthlyVolumes['июнь'] = values.monthJune;
            if (values.monthJuly) monthlyVolumes['июль'] = values.monthJuly;
            if (values.monthAugust) monthlyVolumes['август'] = values.monthAugust;
            if (values.monthSeptember) monthlyVolumes['сентябрь'] = values.monthSeptember;
            if (values.monthOctober) monthlyVolumes['октябрь'] = values.monthOctober;

            const applicationData = {
                crops: values.crops,
                plannedWaterVolume: values.plannedWaterVolume,
                startDate: values.dateRange[0].format('YYYY-MM-DD'),
                endDate: values.dateRange[1].format('YYYY-MM-DD'),
                channelNumber: values.channelNumber,
                channelName: values.channelName,
                specificWaterConsumptionRate: values.specificWaterConsumptionRate,
                irrigatedArea: values.irrigatedArea,
                location: values.location,
                cadastralNumber: values.cadastralNumber,
                waterMeteringMethod: values.waterMeteringMethod,
                monthlyWaterVolumes: Object.keys(monthlyVolumes).length > 0 ? JSON.stringify(monthlyVolumes) : undefined
            };

            await ApplicationService.createApplication(applicationData);
            message.success(<IntlMessage id={'dashboard.user.applications.createSuccess'} fallback={'Заявка успешно создана'} />);
            form.resetFields();
            fetchMyApplications();
        } catch (error) {
            message.error(<IntlMessage id={'dashboard.user.applications.createError'} fallback={'Не удалось создать заявку'} />);
        } finally {
            setSubmitting(false);
        }
    };

    const handleSignApplication = async (applicationId) => {
        const userIin = user?.profile?.iin;
        const userFullName = user?.profile?.fullName;

        if (!userIin) {
            message.error(<IntlMessage id={'dashboard.user.applications.noIin'} fallback={'ИИН пользователя не найден'} />);
            return;
        }

        if (!userFullName) {
            message.error(<IntlMessage id={'dashboard.user.applications.noFullName'} fallback={'ФИО пользователя не найдено'} />);
            return;
        }

        setSigningLoading({...signingLoading, [applicationId]: true});
        try {
            const success = await signApplication(applicationId, userIin, userFullName);
            if (success) {
                fetchMyApplications();
            }
        } catch (error) {
            console.error('Error signing application:', error);
        } finally {
            setSigningLoading({...signingLoading, [applicationId]: false});
        }
    };

    const handleDownloadContract = async (applicationId) => {
        setDownloadLoading({...downloadLoading, [applicationId]: true});
        try {
            const response = await ApplicationService.getContractDraft(applicationId);
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'dogovor-draft.pdf';
            a.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            message.error(t('dashboard.user.applications.downloadError'));
        } finally {
            setDownloadLoading({...downloadLoading, [applicationId]: false});
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case APPLICATION_STATUS.SUBMITTED: return 'processing';
            case APPLICATION_STATUS.APPROVED: return 'success';
            case APPLICATION_STATUS.REJECTED: return 'error';
            case APPLICATION_STATUS.CONTRACT_CONCLUSION: return 'warning';
            case APPLICATION_STATUS.COMPLETED: return 'success';
            default: return 'default';
        }
    };

    const renderApplicationItem = (item) => {
        const actions = [];

        if (item.status === APPLICATION_STATUS.CONTRACT_CONCLUSION) {
            actions.push(
                <Button
                    key="download"
                    type="default"
                    size="small"
                    icon={<DownloadOutlined />}
                    loading={downloadLoading[item.id]}
                    onClick={() => handleDownloadContract(item.id)}
                >
                    <IntlMessage id={'dashboard.user.applications.downloadContract'} fallback={'Скачать договор'} />
                </Button>,
                <Button
                    key="sign"
                    type="primary"
                    size="small"
                    icon={<SafetyCertificateOutlined />}
                    loading={signingLoading[item.id]}
                    onClick={() => handleSignApplication(item.id)}
                >
                    <IntlMessage id={'dashboard.user.applications.sign'} fallback={'Подписать'} />
                </Button>
            );
        }

        if (item.status === APPLICATION_STATUS.COMPLETED) {
            actions.push(
                <Button
                    key="download"
                    type="default"
                    size="small"
                    icon={<DownloadOutlined />}
                    loading={downloadLoading[item.id]}
                    onClick={() => handleDownloadContract(item.id)}
                >
                    <IntlMessage id={'dashboard.user.applications.downloadContract'} fallback={'Скачать договор'} />
                </Button>
            );
        }

        return (
            <List.Item actions={actions}>
                <List.Item.Meta
                    title={
                        <Space>
                            {item.crops}
                            <Badge
                                status={getStatusColor(item.status)}
                                text={getStatusLabel(item.status, t)}
                            />
                        </Space>
                    }
                    description={
                        <>
                            <div>
                                <strong>{t('dashboard.application.field.crops')}:</strong> {item.crops}<br/>
                                <strong>{t('dashboard.application.field.plannedWaterVolume')}:</strong> {item.plannedWaterVolume} м³<br/>
                                <strong>{t('dashboard.application.field.period')}:</strong> {dayjs(item.startDate).format('DD.MM.YYYY')} - {dayjs(item.endDate).format('DD.MM.YYYY')}
                                {item.channelName && <><br/><strong>{t('dashboard.application.field.channel')}:</strong> {item.channelName}</>}
                                {item.channelNumber && <> (№{item.channelNumber})</>}
                                {item.irrigatedArea && <><br/><strong>{t('dashboard.application.field.irrigatedArea')}:</strong> {item.irrigatedArea} га</>}
                                {item.location && <><br/><strong>{t('dashboard.application.field.location')}:</strong> {item.location}</>}
                                {item.cadastralNumber && <><br/><strong>{t('dashboard.application.field.cadastralNumber')}:</strong> {item.cadastralNumber}</>}
                            </div>
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                                {t('dashboard.application.createdAt')}: {dayjs(item.createdAt).format('DD.MM.YYYY HH:mm')}
                            </div>
                        </>
                    }
                />
            </List.Item>
        );
    };

    const CreateApplicationForm = () => (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleCreateApplication}
        >
            <Row gutter={16}>
                <Col xs={24} md={12}>
                    <Form.Item
                        name="crops"
                        label={t('dashboard.application.form.cropType')}
                        rules={[{ required: true, message: t('dashboard.application.form.cropTypeRequired') }]}
                    >
                        <Input placeholder={t('dashboard.application.form.cropTypePlaceholder')} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item
                        name="plannedWaterVolume"
                        label={t('dashboard.application.form.plannedVolume')}
                        rules={[{ required: true, message: t('dashboard.application.form.plannedVolumeRequired') }]}
                    >
                        <InputNumber style={{ width: '100%' }} placeholder={t('dashboard.application.form.plannedVolumePlaceholder')} min={0} />
                    </Form.Item>
                </Col>
            </Row>

            <Form.Item
                name="dateRange"
                label={t('dashboard.application.form.usagePeriod')}
                rules={[{ required: true, message: t('dashboard.application.form.usagePeriodRequired') }]}
            >
                <RangePicker style={{ width: '100%' }} />
            </Form.Item>

            <Row gutter={16}>
                <Col xs={24} md={12}>
                    <Form.Item name="channelNumber" label={t('dashboard.application.form.channelNumber')}>
                        <Input placeholder={t('dashboard.application.form.channelNumberPlaceholder')} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item name="channelName" label={t('dashboard.application.form.channelName')}>
                        <Input placeholder={t('dashboard.application.form.channelNamePlaceholder')} />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col xs={24} md={12}>
                    <Form.Item name="specificWaterConsumptionRate" label={t('dashboard.application.form.specificRate')}>
                        <InputNumber style={{ width: '100%' }} placeholder="м³" min={0} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item name="irrigatedArea" label={t('dashboard.application.form.irrigatedArea')}>
                        <InputNumber style={{ width: '100%' }} placeholder="га" min={0} />
                    </Form.Item>
                </Col>
            </Row>

            <Form.Item name="location" label={t('dashboard.application.form.location')}>
                <Input placeholder={t('dashboard.application.form.locationPlaceholder')} />
            </Form.Item>

            <Row gutter={16}>
                <Col xs={24} md={12}>
                    <Form.Item name="cadastralNumber" label={t('dashboard.application.form.cadastralNumber')}>
                        <Input placeholder={t('dashboard.application.form.cadastralNumberPlaceholder')} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item name="waterMeteringMethod" label={t('dashboard.application.form.waterMetering')}>
                        <Input placeholder={t('dashboard.application.form.waterMeteringPlaceholder')} />
                    </Form.Item>
                </Col>
            </Row>

            <Card size="small" title={t('dashboard.application.form.monthlyVolumes')} style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                    <Col xs={12} md={6}>
                        <Form.Item name="monthMarch" label={t('dashboard.application.form.month.march')}>
                            <InputNumber style={{ width: '100%' }} min={0} />
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Item name="monthApril" label={t('dashboard.application.form.month.april')}>
                            <InputNumber style={{ width: '100%' }} min={0} />
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Item name="monthMay" label={t('dashboard.application.form.month.may')}>
                            <InputNumber style={{ width: '100%' }} min={0} />
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Item name="monthJune" label={t('dashboard.application.form.month.june')}>
                            <InputNumber style={{ width: '100%' }} min={0} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col xs={12} md={6}>
                        <Form.Item name="monthJuly" label={t('dashboard.application.form.month.july')}>
                            <InputNumber style={{ width: '100%' }} min={0} />
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Item name="monthAugust" label={t('dashboard.application.form.month.august')}>
                            <InputNumber style={{ width: '100%' }} min={0} />
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Item name="monthSeptember" label={t('dashboard.application.form.month.september')}>
                            <InputNumber style={{ width: '100%' }} min={0} />
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Item name="monthOctober" label={t('dashboard.application.form.month.october')}>
                            <InputNumber style={{ width: '100%' }} min={0} />
                        </Form.Item>
                    </Col>
                </Row>
            </Card>
        </Form>
    );

    return (
        <Card
            title={
                <>
                    <FileTextOutlined style={{ marginRight: 8 }} />
                    <IntlMessage id={'dashboard.user.applications.title'} fallback={'Мои заявки'} />
                </>
            }
            extra={
                <Button
                    type="default"
                    icon={<ReloadOutlined />}
                    onClick={fetchMyApplications}
                    loading={loading}
                >
                    <IntlMessage id={'refresh'} fallback={'Обновить'} />
                </Button>
            }
            style={{ height: '100%' }}
        >
            <Tabs defaultActiveKey="my">
                <TabPane
                    tab={
                        <Badge count={applications.length} size="small">
                            <IntlMessage id={'dashboard.user.applications.my'} fallback={'Мои заявки'} />
                        </Badge>
                    }
                    key="my"
                >
                    <List
                        loading={loading}
                        dataSource={applications}
                        renderItem={renderApplicationItem}
                        locale={{
                            emptyText: <IntlMessage id={'dashboard.user.applications.empty'} fallback={'У вас пока нет заявок'} />
                        }}
                    />
                </TabPane>

                <TabPane
                    tab={
                        <>
                            <PlusOutlined />
                            <IntlMessage id={'dashboard.user.applications.create'} fallback={'Создать заявку'} />
                        </>
                    }
                    key="create"
                >
                    <div style={{ width: '100%' }}>
                        <CreateApplicationForm />
                        <div style={{ marginTop: 24, textAlign: 'right' }}>
                            <Button
                                type="primary"
                                onClick={() => form.submit()}
                                loading={submitting}
                                icon={<PlusOutlined />}
                            >
                                <IntlMessage id={'dashboard.user.applications.submit'} fallback={'Подать заявку'} />
                            </Button>
                        </div>
                    </div>
                </TabPane>
            </Tabs>
        </Card>
    );
};

export default UserApplications;
